
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { RefreshCw, Mail } from 'lucide-react';
import { getErrorMessage } from '@/utils/errorHandling';

export const OutlookEmailConnectButton = () => {
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnectOutlookEmail = async () => {
    setIsConnecting(true);
    toast.message("Starting Outlook email connection…");

    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error("You must be signed in to connect an Outlook account for email sync.");
      }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/outlook-email-oauth?authorize=1`,
        {
          method: 'GET', // Explicitly GET for authorize step
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: "Failed to parse error response" }));
        console.error("Error initiating Outlook Email OAuth:", errorData);
        throw new Error(errorData.error || "Failed to initiate Outlook Email OAuth");
      }

      const { authUrl } = await res.json();

      if (authUrl) {
        // Mark that an Outlook email OAuth flow is pending
        localStorage.setItem('oauth_pending_flow', 'outlook_email_sync');
        localStorage.setItem('oauth_redirect_uri', window.location.pathname + window.location.search); // Store current page to return to
        
        console.log(`Redirecting to Microsoft auth for email sync at ${new Date().toISOString()}`);
        window.location.href = authUrl;
      } else {
        throw new Error("No authentication URL returned from outlook-email-oauth function");
      }
    } catch (error) {
      console.error("Outlook Email OAuth initialization error:", error);
      const message = getErrorMessage(error);
      toast.error(message);
      setIsConnecting(false);
    }
    // No setIsConnecting(false) here because of redirect
  };

  return (
    <div className="p-4 border rounded-lg">
      <h3 className="text-md font-medium mb-2">Sync Outlook Emails</h3>
      <p className="text-sm text-muted-foreground mb-3">
        Connect your Outlook account to enable email synchronization and processing.
      </p>
      <Button
        onClick={handleConnectOutlookEmail}
        disabled={isConnecting}
        className="w-full sm:w-auto"
      >
        {isConnecting ? (
          <>
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            Redirecting…
          </>
        ) : (
          <>
            <Mail className="h-4 w-4 mr-2" />
            Connect Outlook for Email Sync
          </>
        )}
      </Button>
       <p className="text-xs text-muted-foreground mt-2">
        Note: This will connect an individual Outlook account specifically for email synchronization. 
        It is separate from the general Microsoft 365 integration.
      </p>
    </div>
  );
};
